import { Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Panel } from '#features/ui';
import { toaster } from '#features/ui/components/Toaster';
import { VoiceParticipantRow } from '#features/voice/components/VoiceWidget/VoiceParticipantRow';

/** Local visual fixture: real moderation UI, no session or media requests. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/session-removal')({
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    mode: search.mode === 'error' || search.mode === 'pending' ? search.mode : 'success',
    role: search.role === 'participant' || search.role === 'self' ? search.role : 'author',
    long: search.long === true || search.long === 'true',
  }),
  component: SessionRemovalPreview,
  ssr: false,
});

function SessionRemovalPreview() {
  const search = Route.useSearch();
  const i18n = useMemo(
    () =>
      setupI18n({
        locale: search.lang,
        messages: { ru: ruMessages, en: enMessages },
      }),
    [search.lang]
  );
  return import.meta.env.DEV ? (
    <I18nProvider i18n={i18n}>
      <RemovalFixture key={JSON.stringify(search)} {...search} />
    </I18nProvider>
  ) : null;
}

type FixtureProps = { lang: 'ru' | 'en'; mode: string; role: string; long: boolean };
const RemovalFixture = ({ mode, role, long }: FixtureProps) => {
  const { t } = useLingui();
  const [removed, setRemoved] = useState(false);
  const name = long ? 'NovaОченьДлинноеИмяУчастникаСессииБезПробелов' : 'Nova';
  const remove = async () => {
    if (mode === 'pending') await new Promise<void>(() => undefined);
    if (mode === 'error') {
      toaster.create({
        title: t`post:voice.moderationFailed`,
        description: t`Could not remove the participant. Please try again.`,
        type: 'error',
      });
      throw new Error('Preview removal failed');
    }
    setRemoved(true);
  };
  return (
    <Stack p={4} gap={4} maxW="440px" w="full" mx="auto" data-testid="session-removal-preview">
      <Text textStyle="caption" color="fg.secondary">
        TP-676 · local fixture · {role} · {mode}
      </Text>
      <Panel variant="object" p={2}>
        <VoiceParticipantRow
          member={{
            userId: 'preview-host',
            displayName: 'Salt',
            host: true,
            connected: true,
            muted: false,
            speaking: true,
          }}
          isSelf
        />
        {!removed ? (
          <VoiceParticipantRow
            member={{
              userId: 'preview-member',
              displayName: name,
              host: false,
              connected: true,
              muted: false,
              speaking: false,
            }}
            isSelf={role === 'self'}
            onMuteForMe={role === 'self' ? undefined : () => undefined}
            onMuteForAll={role === 'author' ? () => undefined : undefined}
            onRemove={role === 'author' ? remove : undefined}
          />
        ) : null}
      </Panel>
      <Text textStyle="caption" color="fg.secondary" data-testid="remaining-session-status">
        LIVE · {removed ? '1/2' : '2/2'}
      </Text>
    </Stack>
  );
};
